/**
 * Fechas "date-only" (YYYY-MM-DD) del negocio. Una sola implementación.
 *
 * Auditoría 18/09/2026: "hoy" estaba escrito 7 veces (todayARISO, todayIso,
 * hoyBuenosAires, todayISO ×2, todayLocal, todayMidnightAR) con TRES semánticas
 * distintas — UTC, Buenos Aires y zona del navegador — y la semana operativa
 * (lunes–domingo AR) vivía duplicada byte a byte en frontend/src/lib/week.ts y
 * backend/src/lib/week-range.ts. Mezclarlas produce el clásico "abre a las 21hs
 * del día anterior" (D-Talk, 16/09).
 *
 * Reglas:
 *  - `todayAR()` es el "hoy" del negocio (ventanas, plazos, ingresos, semanas).
 *  - `todayUTC()` SOLO para datasets que ya están particionados por día UTC
 *    (telemetría interna, FinOps de AWS/GCP/Vercel). Nombrarlo explícito evita
 *    el `toISOString().slice(0, 10)` disfrazado de "hoy".
 *  - `todayLocal()` es la zona del navegador: solo para defaults de formularios
 *    que la persona ve y corrige (vencimiento de una tarjeta del Kanban).
 *  - Las comparaciones son por STRING: convertir un date-only a `Date` en un
 *    browser UTC-3 corre la fecha un día.
 *
 * Pura, sin dependencias: la usan backend, frontend y `shared/`.
 * Tests: `npm run test:dates`.
 */

export const BUENOS_AIRES_TZ = 'America/Argentina/Buenos_Aires';

/** Hoy en Buenos Aires como 'YYYY-MM-DD'. `now` inyectable para tests. */
export function todayAR(now: Date = new Date()): string {
  // 'en-CA' formatea yyyy-mm-dd; formatToParts por si algún runtime mete espacios raros.
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: BUENOS_AIRES_TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now); // reuse-ok: ESTA es la implementación compartida
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Hoy en UTC. Solo para datos que YA están particionados por día UTC (ver cabecera). */
export function todayUTC(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10); // reuse-ok: ESTA es la implementación compartida
}

/** Hoy en la zona del navegador/proceso. Solo para defaults visibles de formularios. */
export function todayLocal(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/** 'YYYY-MM-DD' ± n días, aritmética en UTC (no hay DST que corra el resultado). */
export function addDaysISO(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10); // reuse-ok: aritmética de date-only, no "hoy"
}

/**
 * 'YYYY-MM-DD' + n días hábiles (lunes a viernes; sin feriados: no hay calendario
 * por país). Antes vivía 3 veces (people-learning, people-flow-window, form-config):
 * acá es la única. Aritmética en UTC como addDaysISO.
 */
export function addBusinessDaysISO(iso: string, n: number): string {
  let cur = iso;
  let left = n;
  while (left > 0) {
    cur = addDaysISO(cur, 1);
    const [y, m, d] = cur.split('-').map(Number);
    const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // reuse-ok: este ES el helper de días hábiles
    if (dow !== 0 && dow !== 6) left--;
  }
  return cur;
}

/** Días hábiles desde `from` (exclusivo) hasta `to` (inclusivo). Negativo si `to` ya pasó. */
export function businessDaysBetweenISO(fromIso: string, toIso: string): number {
  const sign = toIso >= fromIso ? 1 : -1;
  let [cur, end] = sign === 1 ? [fromIso, toIso] : [toIso, fromIso];
  let n = 0;
  while (cur < end) {
    cur = addDaysISO(cur, 1);
    const [y, m, dd] = cur.split('-').map(Number);
    const dow = new Date(Date.UTC(y, m - 1, dd)).getUTCDay(); // reuse-ok: este ES el helper de días hábiles
    if (dow !== 0 && dow !== 6) n++;
  }
  return sign * n;
}

/**
 * Mismo día N meses antes/después (date-only). Si el día no existe en el mes
 * destino (31/03 − 1 mes) cae al último día (28/02). Para ventanas "menos de
 * N meses" (Analytics del Mapa de Talento) comparando ISO como string.
 */
export function addMonthsISO(iso: string, months: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const first = new Date(Date.UTC(y, m - 1 + months, 1));
  const lastDay = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  first.setUTCDate(Math.min(d, lastDay));
  return first.toISOString().slice(0, 10);
}

/** 'yyyy-mm-dd' → 'dd/mm/yyyy' para mostrar en UI (sin hora, sin tz). Vacío ⇒ ''. */
export function toDMY(iso?: string | null): string {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
}

/** Días enteros entre dos date-only (b − a). */
export function daysBetweenISO(a: string, b: string): number {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000);
}

/** Medianoche LOCAL de un date-only, para comparar con Dates locales (planillas). */
export function localMidnightOf(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Fecha larga en español ("21 de septiembre de 2026") de un date-only, sin el
 * corrimiento de un día de `new Date('YYYY-MM-DD')` en América. Reemplaza los
 * `toLocaleDateString('es-AR', …)` sueltos (mails, DMs, PDFs).
 */
export function formatFechaLargaEs(iso: string): string {
  return localMidnightOf(iso).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' });
}

// ── Semana operativa: lunes a domingo, en Buenos Aires ────────────────────────

export interface WeekRange {
  weekStart: string; // YYYY-MM-DD (lunes)
  weekEnd: string; // YYYY-MM-DD (domingo)
}

/** Semana (lun–dom) que contiene un date-only. */
export function weekRangeOf(iso: string): WeekRange {
  const [y, m, d] = iso.split('-').map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = domingo
  const mondayOffset = dow === 0 ? -6 : 1 - dow;
  return { weekStart: addDaysISO(iso, mondayOffset), weekEnd: addDaysISO(iso, mondayOffset + 6) };
}

export function getCurrentWeekRangeAR(now: Date = new Date()): WeekRange {
  return weekRangeOf(todayAR(now));
}

/** ¿El día de un ISO (o date-only) cae dentro del rango, inclusive? */
export function isoDateInRange(isoOrNull: string | null | undefined, range: { weekStart: string; weekEnd: string }): boolean {
  if (!isoOrNull) return false;
  const day = isoOrNull.slice(0, 10);
  return day >= range.weekStart && day <= range.weekEnd;
}

/** Los próximos N días desde hoy (AR), ambos inclusive. */
export function nextDaysRangeAR(days: number, now: Date = new Date()): { from: string; to: string } {
  const from = todayAR(now);
  return { from, to: addDaysISO(from, days - 1) };
}

/** "2026-08-03" → "03/08" sin pasar por Date (evita el corrimiento de timezone). */
export function formatDayMonth(isoDate: string): string {
  const [, m, d] = isoDate.split('-');
  return `${d}/${m}`;
}

/* ───────────────────────── Meses 'YYYY-MM' ─────────────────────────
 * Aritmética sobre meses (sin día): cobertura de plan, prorrateo de deals. Distinto de
 * addMonthsISO (fecha completa con clamp de día). backend/src/lib/finops/read/dates.ts
 * tiene otro addMonths sobre 'YYYY-MM' anterior a esto (misma operación: migrar cuando se toque). */

/** 'YYYY-MM' ± n meses. */
export function addMonthsYM(ym: string, n: number): string {
  const [y, m] = ym.split('-').map(Number);
  const idx = y * 12 + (m - 1) + n;
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`;
}

/** Todos los meses entre dos 'YYYY-MM' inclusive. */
export function monthsBetweenYM(fromYm: string, toYm: string): string[] {
  const out: string[] = [];
  for (let m = fromYm; m <= toYm; m = addMonthsYM(m, 1)) out.push(m);
  return out;
}

/** Último día del mes de un 'YYYY-MM' (date-only). */
export function endOfMonthYM(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  return `${ym}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, '0')}`;
}
